import React from "react";
import {BrowserRouter, Switch, Route, Redirect, useLocation, useHistory} from "react-router-dom";
import useNavigationInactivity from './hooks/useNavigationInactivity';
import {Container} from "react-bootstrap";
import {PrivateRoute, NavbarLeft, QueuesList, ProfilePage, TopNavigationBar, MainLayout} from "./components";
import styles from './App.module.scss';
import {LoginForm} from "./features/authentication/LoginForm";
import {GoogleCallback} from "./features/authentication/GoogleCallback";
import {NotificationPage} from "./features/notifications/NotificationPage";
import 'react-toastify/dist/ReactToastify.min.css'
import {ToastContainer} from 'react-toastify'
import {ProjectsList} from "./features/projects/ProjectsList";
import {SingleProjectPage} from "./features/projects/SingleProjectPage";
import {ReportPage} from "./features/mediums/ReportPage";
import {Attendance} from "./features/attendance/Attendance";
import {Policy} from "./features/policy/policy";
import {PolicyForm} from "./features/policy/PolicyForm";
import {CategoryList} from "./features/category/CategoryList";
import {CategoryForm} from "./features/category/CategoryForm";
import {StatisticsPage} from "./features/statistics/StatisticsPage";
import UserManualPage from "./features/usermanual/UserManual";
import {UserManualList} from "./features/usermanual/UserManualList";
import {UserManualForm} from "./features/usermanual/UserManualForm";
import { setAttendanceTracking } from './features/authentication/attendance';
import { broadcastActivity } from './utils/activityBroadcast';

const AppContent = () => {
    const location = useLocation();
    const history = useHistory();

    const logoutFn = () => {
        try {
            const { client } = require('./api/client');
            // Track attendance before logout (report session end)
            setAttendanceTracking((endpoint, body) => client.post(endpoint, body))
                .finally(() => {
                    // Attempt to call backend logout endpoint
                    client.post('logout', {}).finally(() => {
                        client.logout();
                        window.location.href = '/login';
                    }).catch(() => {
                        // If logout endpoint fails, still clear client auth
                        client.logout();
                        window.location.href = '/login';
                    });
                });
        } catch (e) {
            try {
                const { client } = require('./api/client');
                client.logout();
            } catch (err) {}
            window.location.href = '/login';
        }
    };

    const { resetInactivityTimer } = useNavigationInactivity(logoutFn);

    // reset inactivity timer on every route change (navigation = activity)
    history.listen(() => {
        resetInactivityTimer();
        broadcastActivity();
    });

    return (
        <React.Fragment>
            <TopNavigationBar />
            <NavbarLeft />
            {location.pathname === "/" && (
                <div className={styles['user-status-fixed']}>
                    <Attendance />
                </div>
            )}
            <MainLayout>
                <main role="main" className={`main-content-mobile ${styles['main-content']}`}>
                    <Container fluid>
                        <Switch>
                            <PrivateRoute exact path="/" component={QueuesList}/>
                            <PrivateRoute exact path="/projects" component={ProjectsList}/>
                            <PrivateRoute exact path="/projects/:projectId" component={SingleProjectPage}/>
                            <PrivateRoute exact path="/statistics" component={StatisticsPage}/>
                            <PrivateRoute exact path="/notifications" component={NotificationPage}/>
                            <PrivateRoute exact path="/user-manual" component={UserManualPage}/>
                            <PrivateRoute exact path="/profile" component={ProfilePage}/>
                            <Route path="/login" component={LoginForm}/>
                            <Route path="/auth/google/callback" component={GoogleCallback}/>
                            <Route path="/report" component={ReportPage}/>
                            <PrivateRoute exact path="/policy" component={Policy}/>
                            <PrivateRoute path="/policy/create" component={PolicyForm} />
                            <PrivateRoute path="/policy/update/:id" component={PolicyForm} />
                            <PrivateRoute exact path="/category" component={CategoryList}/>
                            <PrivateRoute path="/category/create" component={CategoryForm} />
                            <PrivateRoute path="/category/edit/:id" component={CategoryForm} />
                            <PrivateRoute exact path="/user-manual/list" component={UserManualList}/>
                            <PrivateRoute path="/user-manual/create" component={UserManualForm} />
                            <PrivateRoute path="/user-manual/edit/:id" component={UserManualForm} />
                            <Redirect from="*" to="/" />
                        </Switch>
                    </Container>
                </main>
            </MainLayout>
        </React.Fragment>
    );
};

function App() {
    return (
        <BrowserRouter>
            <AppContent />
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </BrowserRouter>
    );
}

export default App